import React from 'react';
import { useQuery } from '@apollo/client';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import CalendarPicker from '../../../components/CalendarPicker';
import BillingAddress from '../../../components/V2/BillingAddress';
import DeliveryDay from '../UserManagementPage/DeliveryDay';
import ProgramDetails from './components/ProgramDetails';
import { FETCH_USER_DELIVERY_SKIPS, GET_PROGRAM_DETAIL } from 'core/api/apollo/queries';
import { NavigationHeader } from 'onramp/ui/components';
import { Button, Box, Flex, Hr, Text } from 'core/ui/atoms';
import { mySettingsPath } from 'onramp/api/navigation';
import SplitPageSidebar from 'core/ui/components/SplitPageSidebar';
import ResponsiveView from 'core/ui/components/ResponsiveView';

const MyProgramPage = () => {
  const history = useHistory();
  const { data: { fetchUserDeliverySkips = [] } = [], refetch: refetchSkips } = useQuery(FETCH_USER_DELIVERY_SKIPS);

  const { loading: programLoading, error: programError, data: { getProgramDetail = {} } = {} } = useQuery(GET_PROGRAM_DETAIL);

  const onSettingsNavigate = () => {
    history.push(mySettingsPath());
  };

  const isSubscription = getProgramDetail?.subscriptionActive;
  const daysPerWeek = getProgramDetail?.programBundle?.schedule?.daysOfWeek?.length;

  const RenderSideBar = () => (
    <CalendarContainer>
      <Title>My Calendar</Title>
      <SubTitle>
        Select a week to skip your program. If you'd like to make changes later,
        unskip that week first to edit your order.
      </SubTitle>
      <CalendarPicker
        daysPerWeek={daysPerWeek}
        refetchSkips={refetchSkips}
        deliverySkips={fetchUserDeliverySkips}
      />
    </CalendarContainer>
  );

  const RenderHeader = ({ isDesktop }) => (
    <ResponsiveView desktop={isDesktop}>
      <NavigationHeader title='My Program' />
      <Hr />
    </ResponsiveView>
  );

  RenderHeader.propTypes = {
    isDesktop: PropTypes.bool,
  };

  return (
    <Box>
      <RenderHeader isDesktop={true}  />
      <SplitPageSidebar sidebarWidth='40%' sidebarView={<RenderSideBar />}>
        <RenderHeader isDesktop={false} />
        <Content>
          <Title>My Program Details</Title>
          <ProgramDetails
            getProgramDetail={getProgramDetail}
            programLoading={programLoading}
            programError={programError}
          />
          {isSubscription && <FlexRowContainer direction='row' alignItems='center' justifyContent='center'>
            <SubscriptionButton btn-outline onClick={onSettingsNavigate}>
              <Text>MANAGE MY SUBSCRIPTION</Text>
            </SubscriptionButton>
          </FlexRowContainer>}
          <Hr />
          <ResponsiveView desktop={false}>
            <CalendarContainer>
              <Title>My Calendar</Title>
              <SubTitle>
                Select a week to skip your program. If you'd like to make changes later,
                unskip that week first to edit your order.
              </SubTitle>
              <CalendarPicker
                daysPerWeek={daysPerWeek}
                refetchSkips={refetchSkips}
                deliverySkips={fetchUserDeliverySkips}
              />
            </CalendarContainer>
            <Hr />
          </ResponsiveView>
          <AddressContainer>
            <Title>Default Delivery Address</Title>
            <BillingAddress />
          </AddressContainer>
          <ResponsiveView desktop={true}>
            <DeliveryDay isDefault={true} />
          </ResponsiveView>
        </Content>
        <ResponsiveView desktop={false}>
          <DeliveryDay />
        </ResponsiveView>
      </SplitPageSidebar>
    </Box>
  );
};

const FlexRowContainer = styled(Flex)`
  margin-top: 36px;
  margin-bottom: 44px;
`;

const SubscriptionButton = styled(Button)`
  width: 320px;
  height: 43px;
  padding: 10px 32px;
  gap: 10px;
  border-radius: 4px;
  p {
    margin: 0px;
    font-size: 12px;
    text-transform: uppercase;
    font-family: ${({ theme }) => theme.typography.familyPoppins};
    color: ${(p) => p.theme.colors.primaryV2};
  }
`;

const AddressContainer = styled(Box)`
`;

const SubTitle = styled(Text)`
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
  text-align: center;
  margin-top: 0px;
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  color: ${(p) => p.theme.colors.primaryV2};
`;

const CalendarContainer = styled(Box)`
  padding-right: 100px;
  ${(p) => p.theme.max('sm')`
    margin-bottom: 30px;
    padding-left: 0px;
    padding-right: 0px;
  `}
`;

const Content = styled(Box)`
  padding-left: 16px;
  padding-right: 16px;
  width: 80%;
  ${(p) => p.theme.max('sm')`
    width: 100%;
  `}
`;

const Title = styled(Text)`
  text-align: center;
  font-size: 22px;
  font-weight: 400;
  line-height: 28px;
  font-family: ${({ theme }) => theme.typography.familyLarken};
  color: ${(p) => p.theme.colors.primaryV2};
`;

export default MyProgramPage;
